/**
 * Users-tab state and actions: the account-type filter, per-user usage reset
 * and account deletion.
 */
import { useState } from 'react';
import { apiRequest } from '../../../../services/apiClient';
import { confirmDialog, showDialog } from '../../../../utils/dialogs';
import { useToast } from '../../../../context/ToastContext';
import type { User } from '../../../../utils/roleUtils';
import type { Translate } from '../platformAdminFormat';
import type { PlatformUser, UserAccountFilter } from '../platformAdminTypes';

export function useUserActions(
  t: Translate,
  user: User | undefined,
  loadPlatformData: () => Promise<void>,
) {
  const { showToast } = useToast();
  const [userFilter, setUserFilter] = useState<UserAccountFilter>('all');
  const [resettingUserId, setResettingUserId] = useState<number | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);

  const handleResetUsage = async (student: PlatformUser) => {
    const confirmationMessage = t('platformAdmin.resetUsageConfirm', {
      name: student.full_name || student.email,
    });

    const confirmed = await confirmDialog(
      t('platformAdmin.resetUsage'),
      confirmationMessage,
      t('common.confirm'),
      t('common.cancel'),
    );

    if (!confirmed) return;

    try {
      setResettingUserId(student.id);
      await apiRequest({
        endpoint: `/user-profile/admin/users/${student.id}/reset-usage`,
        method: 'POST',
      });

      showToast(t('platformAdmin.resetUsageSuccess'), 'success');
      await loadPlatformData();
    } catch (error) {
      console.error('Failed to reset usage limits', error);
      const message = t('platformAdmin.resetUsageError');
      showDialog(message);
    } finally {
      setResettingUserId(null);
    }
  };

  const handleDeleteUser = async (userItem: PlatformUser) => {
    const confirmationMessage = t('platformAdmin.deleteUserConfirm', {
      email: userItem.email,
    });

    const confirmed = await confirmDialog(
      t('platformAdmin.deleteUser'),
      confirmationMessage,
      t('common.confirm'),
      t('common.cancel'),
    );

    if (!confirmed) return;

    try {
      setDeletingUserId(userItem.id);
      await apiRequest({
        endpoint: `/auth/admin/users/${userItem.id}`,
        method: 'DELETE',
        data: {
          reason: `Deleted via platform admin panel${user?.email ? ` by ${user.email}` : ''}`,
        },
      });

      const successMessage = t('platformAdmin.deleteUserSuccess');
      showToast(successMessage, 'success');

      await loadPlatformData();
    } catch (error) {
      console.error('Failed to delete user', error);
      const message = t('platformAdmin.deleteUserError');
      showDialog(message);
    } finally {
      setDeletingUserId(null);
    }
  };

  return {
    userFilter,
    setUserFilter,
    resettingUserId,
    deletingUserId,
    handleResetUsage,
    handleDeleteUser,
  };
}

export type UserActions = ReturnType<typeof useUserActions>;
